import { LockOutlined } from '@ant-design/icons';
import { IDEA_STATUSES, canModerateIdeas } from '@cult/domain';
import type { IdeaComment, IdeaStatus, PublicUser } from '@cult/schemas';
import { Alert, App as AntApp, Skeleton, Tag, Typography } from 'antd';
import { useState } from 'react';
import { InlineError } from '../../../../components';
import { CommentComposer, CommentList, CommentRow } from '../../../comments';

import { useCreateComment, useDeleteComment, useIdeaComments } from '../../hooks';
import styles from './CommentsThread.module.scss';

const { Text } = Typography;

type Props = {
  ideaId: string;
  enabled: boolean;
  currentUser: PublicUser | null;
  ideaStatus: IdeaStatus;
  isMyIdea: boolean;
};

export const CommentsThread = ({ ideaId, enabled, currentUser, ideaStatus, isMyIdea }: Props) => {
  const query = useIdeaComments(ideaId, { enabled });
  const create = useCreateComment();
  const { message } = AntApp.useApp();
  const [draft, setDraft] = useState('');

  const isPending = ideaStatus === IDEA_STATUSES.PENDING;
  const isModerator = currentUser ? canModerateIdeas(currentUser.role) : false;
  const canComment = !isPending || isModerator || isMyIdea;

  const postComment = () => {
    const body = draft.trim();
    if (!body) return;
    create.mutate(
      { ideaId, body },
      {
        onSuccess: () => {
          setDraft('');
        },
        onError: (err) => message.error(err.message),
      },
    );
  };

  if (!enabled || query.isPending) {
    return <Skeleton active paragraph={{ rows: 5 }} />;
  }
  if (query.isError) {
    return <InlineError title="Couldn't load comments" detail={query.error.message} />;
  }

  const comments = query.data?.pages.flatMap((p) => p.comments) ?? [];

  // Mirror the API rule: comments are accepted only on PENDING (participants) and APPROVED.
  // REJECTED is closed — show the lock note instead of the composer.
  const isClosedStatus = ideaStatus !== IDEA_STATUSES.PENDING && ideaStatus !== IDEA_STATUSES.APPROVED;

  return (
    <div className={styles.thread}>
      {isPending ? (
        <Alert
          type="info"
          showIcon
          message="Private channel"
          description="This conversation is only visible to you and admins."
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {isClosedStatus ? (
        <div className={styles.closedNote}>
          <LockOutlined />
          <Text type="secondary" style={{ fontSize: 13 }}>
            Comments are closed for this idea.
          </Text>
        </div>
      ) : canComment ? (
        <CommentComposer value={draft} onChange={setDraft} onSubmit={postComment} submitting={create.isPending} />
      ) : null}

      <CommentList
        isEmpty={comments.length === 0}
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        onLoadMore={() => query.fetchNextPage()}
      >
        {comments.map((c, i) => (
          <IdeaCommentRow key={c.id} comment={c} index={i} isModerator={isModerator} ideaId={ideaId} />
        ))}
      </CommentList>
    </div>
  );
};

const IdeaCommentRow = ({
  comment,
  index,
  isModerator,
  ideaId,
}: {
  comment: IdeaComment;
  index: number;
  isModerator: boolean;
  ideaId: string;
}) => {
  const del = useDeleteComment();
  const { message } = AntApp.useApp();

  const onDelete = () => {
    del.mutate({ ideaId, commentId: comment.id }, { onError: (err) => message.error(err.message) });
  };

  // Admin sees real name; current user sees "You"; others are shown without a name.
  const displayName = comment.author?.displayName ?? (comment.isMyComment ? 'You' : null);

  return (
    <CommentRow
      name={displayName}
      avatarUrl={comment.author?.avatarUrl}
      createdAt={comment.createdAt}
      body={comment.body}
      index={index}
      badge={
        comment.isIdeaAuthor ? (
          <Tag color="blue" style={{ fontSize: 11, lineHeight: '18px', padding: '0 5px', marginInlineEnd: 0 }}>
            Author
          </Tag>
        ) : undefined
      }
      canDelete={isModerator || comment.isMyComment}
      deleting={del.isPending}
      onDelete={onDelete}
    />
  );
};
